import { DATA_SOURCES } from '@xo-union/tk-component-vendor-typeahead';

import { EventLocation } from '../LegacyModal/EventForm';
import objectifyGoogleAddressComponents, {
  GoogleAddressObject,
  GoogleAddressValues,
} from './objectifyGoogleAddressComponents';

export interface AddressComponents {
  google_place?: GoogleAddressObject;
  local_vendor?: LocalAddressObject;
  user_entry?: UserEntryObject;
}

export interface ParsedBooking {
  id?: string;
  vendor_profile_id?: string | null;
  vendor_name?: string | null;
  gdsAddress?: EventLocation;
}

export interface BookingsObject {
  id?: string;
  vendor_name?: string | null;
  vendor_profile_id?: string | null;
  address_components: AddressComponents;
}

export interface AddressObjectResponse {
  id?: string;
  vendor_name?: string | null;
  vendor_profile_id?: string | null;
  address_components: AddressComponents;
  gdsAddress: EventLocation;
}

export interface LocalVendorValues {
  id: string;
  name: string;
  addOns: string | null;
  categories: object[];
  categoryNames: string[];
  claimedStatus: string;
  displayId: string;
  emails: string[];
  phones: object[];
  profileStatus: string;
  purchaseStatus: string;
  qualityTier: string;
  siteUrls: object[];
  location: {
    address: LocalAddressObject;
  };
}

interface UserEntryObject {
  street?: string | null;
  city?: string | null;
  postal_code?: string | null;
  state?: string | null;
  country?: string | null;
}

export interface LocalAddressObject {
  address1?: string;
  address2?: string;
  city?: string;
  isPublic?: string;
  latitude?: string;
  longitude?: string;
  postalCode?: string;
  state?: string;
  country?: string;
}

export const googlePlaceObjectConverter = (googleAddressValues: GoogleAddressValues): AddressObjectResponse => {
  const { place_id: id, name, address_components } = googleAddressValues;

  const {
    postal_code = '',
    locality = '',
    administrative_area_level_1 = '',
    administrative_area_level_2 = '',
    administrative_area_level_3 = '',
    street_number = '',
    route = '',
    country = '',
    sublocality = '',
    neighborhood = '',
    political = '',
    subpremise = '',
  } = objectifyGoogleAddressComponents(address_components);

  const street1 = `${street_number ? `${street_number} ` : ''}${route}`;
  const street2 = '';
  const state = administrative_area_level_1;
  const city = locality || neighborhood || sublocality;
  const zip = postal_code;

  return {
    vendor_profile_id: id,
    vendor_name: name,
    gdsAddress: {
      street1,
      street2,
      zip,
      state,
      city,
      country,
    },
    address_components: {
      google_place: {
        postal_code,
        locality,
        administrative_area_level_1,
        administrative_area_level_2,
        administrative_area_level_3,
        street_number,
        route,
        country,
        sublocality,
        neighborhood,
        political,
        subpremise,
      },
    },
  };
};

const localVendorObjectConverter = (localVendorValues: LocalVendorValues): AddressObjectResponse => {
  const { location, id, name } = localVendorValues;
  const { address } = location;

  const {
    address1 = '',
    address2 = '',
    city = '',
    postalCode = '',
    state = '',
    country = '',
    latitude = '',
    longitude = '',
    isPublic = '',
  } = address;

  return {
    vendor_profile_id: id,
    vendor_name: name,
    gdsAddress: {
      street1: address1 || '',
      street2: address2 || '',
      zip: postalCode || '',
      state: state || '',
      city: city || '',
      country: country || '',
    },
    address_components: {
      local_vendor: {
        address1: address1 || '',
        address2: address2 || '',
        city: city || '',
        postalCode: postalCode || '',
        state: state || '',
        latitude: latitude || '',
        longitude: longitude || '',
        isPublic: isPublic || '',
        country: country || '',
      },
    },
  };
};

// @ts-ignore
export const addressComponentParser = (selectedObject, dataSource): AddressObjectResponse => {
  if (dataSource === DATA_SOURCES.GOOGLE) {
    return googlePlaceObjectConverter(selectedObject);
  }
  return localVendorObjectConverter(selectedObject);
};

export const bookingEntryAddressParser = (bookingObject: BookingsObject): ParsedBooking => {
  const { id, vendor_name, vendor_profile_id, address_components } = bookingObject;
  let street1: string | null = '';
  let street2: string | null = '';
  let state: string | null = '';
  let zip: string | null = '';
  let city: string | null = '';
  let country: string | null = '';

  if (address_components.google_place) {
    const {
      postal_code = '',
      locality = '',
      administrative_area_level_1 = '',
      street_number = '',
      route = '',
      country: google_country = '',
      sublocality = '',
      neighborhood = '',
    } = address_components.google_place;

    street1 = `${street_number ? `${street_number} ` : ''}${route}`;
    street2 = '';
    state = administrative_area_level_1;
    city = locality || neighborhood || sublocality;
    zip = postal_code;
    country = google_country;
  } else if (address_components.local_vendor) {
    const {
      address1 = '',
      address2 = '',
      city: vendor_city = '',
      postalCode = '',
      state: vendor_state = '',
      country: vendor_country = '',
    } = address_components.local_vendor;

    street1 = address1;
    street2 = address2;
    zip = postalCode;
    state = vendor_state;
    city = vendor_city;
    country = vendor_country;
  } else if (address_components.user_entry) {
    const {
      street = '',
      city: user_city = '',
      postal_code = '',
      state: user_state = '',
      country: user_country = '',
    } = address_components.user_entry;

    street1 = street;
    street2 = '';
    city = user_city;
    zip = postal_code;
    state = user_state;
    country = user_country;
  }

  return {
    id,
    vendor_profile_id,
    vendor_name,
    gdsAddress: {
      street1,
      street2,
      city,
      zip,
      state,
      country,
    },
  };
};

export const bookingType = (addressComponents: AddressComponents): string => {
  return Object.keys(addressComponents)[0];
};

export default addressComponentParser;
